import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  IconButton,
  Grid2,
} from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify";
import { TablePaginationCustom } from "../Minimal/src/components/pagination";
import { useTable } from "../Minimal/src/hooks/use-table";
import config from "../common/Config";
import { toast } from "sonner";
import { getCartIncompleteOrders } from "../../data/epos";
import { PLATFORMS, resolveCartChannelId } from "../../data/cart";
import IncompleteOrderPosList from "./IncompleteOrderPosList";
import "../common/commonStyles.css";

const resolveChannelId = () => resolveCartChannelId(PLATFORMS.WEBSITE);

const mapIncompleteOrder = (order) => ({
  id: order.id ?? order.order_id ?? null,
  customerId: order.customer_id ?? order.customerId ?? order.cust_id ?? null,
  customerName: order.name ?? order.customer_name ?? "N/A",
  date: order.created_at ?? order.date ?? "N/A",
  account_no: order.account_no ?? "",
  phone: order.phone ?? order.mobile ?? order.telephone ?? order.customer_phone ?? "",
  email: order.email ?? order.customer_email ?? "",
  postal_code: order.postal_code ?? order.postcode ?? order.zip ?? "",
});

const CustomerIncompleteOrderList = () => {
  const navigate = useNavigate();
  const accessToken = localStorage.getItem("accessToken");
  const user_role = useMemo(() => localStorage.getItem("user_role"), []);
  const channelId = useMemo(() => resolveChannelId(), []);

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  const table = useTable({
    defaultRowsPerPage: 50,
    defaultDense: false,
    defaultOrder: "desc",
    defaultOrderBy: "date",
    defaultSelected: [],
    defaultCurrentPage: 0,
  });

  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getCartIncompleteOrders({
        channel_id: channelId,
        start: table.page * table.rowsPerPage,
        length: table.rowsPerPage,
        search: searchQuery || "",
      });

      const rows = Array.isArray(data?.data)
        ? data.data.map(mapIncompleteOrder)
        : Array.isArray(data)
          ? data.map(mapIncompleteOrder)
          : [];

      setOrders(rows);
      setTotalRecords(
        Number(data?.recordsTotal ?? data?.recordsFiltered ?? rows.length) ||
          rows.length
      );
    } catch (error) {
      toast.error(`Failed to load incomplete orders: ${error.message}`);
      setOrders([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [channelId, table.page, table.rowsPerPage, searchQuery]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchOrders();
  }, [accessToken, fetchOrders]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim();
      setSearchQuery((prev) => {
        if (prev !== nextSearch) {
          table.onChangePage(null, 0);
        }
        return nextSearch;
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const openCustomerCart = (order) => {
    if (!order?.customerId) {
      toast.error("Customer not found for this incomplete order");
      return;
    }

    const customer = {
      id: order.customerId,
      company: order.customerName || "",
      name: order.customerName || "",
      account_no: order.account_no || "",
    };

    if (user_role === "20" || user_role === "8") {
      sessionStorage.setItem(
        "quickOrder_selectedCustomer",
        JSON.stringify(customer)
      );
      navigate("/sales/quick-order");
      return;
    }

    localStorage.setItem("epos_selectedCustomer", JSON.stringify(customer));
    navigate("/sales/epos/new");
  };

  const openNewOrder = () => {
    sessionStorage.removeItem("quickOrder_selectedCustomer");
    localStorage.removeItem("epos_selectedCustomer");
    if (user_role === "18") {
      navigate("/sales/epos/new");
      return;
    }
    navigate("/sales/quick-order");
  };

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <Grid2
              container
              spacing={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}
            >
              <Grid2 size={{ xs: 12, md: 7 }}>
                <Typography className="page-title">
                  Customer Incomplete Order
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md:3  }}>
                <TextField
                  className="input-border"
                  placeholder="Search by customer name"
                  size="small"
                  fullWidth
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify
                            icon="eva:search-outline"
                            width={20}
                            height={20}
                            sx={{ color: "#637381" }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: searchInput ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchInput("")}
                            sx={{ color: "#637381" }}
                          >
                            <Iconify icon="eva:close-fill" width={18} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f7fa",
                      "&:hover": { backgroundColor: "#fff" },
                      "&.Mui-focused": { backgroundColor: "#fff" },
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: "auto" }} display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  variant="contained"
                  className="button-variant-contained"
                  onClick={openNewOrder}
                  size="small"
                  sx={{ whiteSpace: "nowrap", minWidth: "auto", px: 2 }}
                >
                  Add New Order
                </Button>
              </Grid2>
            </Grid2>
          </Box>

          <TableContainer>
            <Table size="medium" className="table-cell-border">
              <TableHead>
                <TableRow className="table-cell-header">
                  <TableCell sx={{ width: "70px" }}>Sr. No.</TableCell>
                  <TableCell sx={{ width: "140px" }}>Date</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell sx={{ width: "140px" }}>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell sx={{ width: "120px" }}>Postal Code</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography sx={{ color: "#637381", fontSize: "0.875rem" }}>
                        No incomplete orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order, index) => (
                    <TableRow
                      key={order.id || index}
                      hover
                      sx={{
                        "& .MuiTableCell-root": {
                          fontSize: "0.75rem",
                          color: "#212b36",
                          py: 1,
                        },
                      }}
                    >
                      <TableCell>
                        {table.page * table.rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell>{order.date}</TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          onClick={() => openCustomerCart(order)}
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textTransform: "none",
                            textDecoration: "underline",
                            justifyContent: "flex-start",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            color: "#212b36",
                          }}
                        >
                          {order.customerName}
                          {order.account_no ? ` (${order.account_no})` : ""}
                        </Button>
                      </TableCell>
                      <TableCell>{order.phone || "-"}</TableCell>
                      <TableCell>{order.email || "-"}</TableCell>
                      <TableCell>{order.postal_code || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 2, py: 1 }}>
            <TablePaginationCustom
              count={totalRecords}
              page={table.page}
              rowsPerPage={table.rowsPerPage}
              onPageChange={table.onChangePage}
              onRowsPerPageChange={table.onChangeRowsPerPage}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

const IncompleteOrderList = () => {
  const user_role = localStorage.getItem("user_role");
  if (user_role === "8") {
    return <CustomerIncompleteOrderList />;
  }
  return <IncompleteOrderPosList />;
};

export default IncompleteOrderList;
